import { useState, useEffect, useCallback } from 'react';
import { Card, Heading, Button, majorScale, Pane, Pagination } from 'evergreen-ui';
import { PlusIcon, CircleArrowLeftIcon } from 'evergreen-ui';
import CommunicationList from './CommunicationList';
import AddCommunicationForm from './AddCommunicationForm';
import EditCommunicationForm from './EditCommunicationForm';
import { useAuth } from '../services/authContext';
import { useLocation } from 'react-router-dom';
import { getCommunicationViews, fetchCommunications } from '../services/communicationServices';
import { useRole } from '../contexts/RoleContext';

const Communications = ({ communications, setCommunications, collaborators }) => {
    const { isOperator, effectiveIsDirector: isDirector } = useRole();
    const { user, isAuthenticated } = useAuth();
    const userId = user?.id;
    const [isAdding, setIsAdding] = useState(false);
    const [editedCommunication, setEditedCommunication] = useState(null);
    const [newCommunication, setNewCommunication] = useState({ title: '', body: '' });
    const [communicationViews, setCommunicationViews] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const location = useLocation();

    const fetchCurrentPage = useCallback(async (p) => {
        try {
            setIsLoading(true);
            const response = await fetchCommunications(userId, isDirector, false, p, pageSize);
            if (response && response.status === 'success') {
                setCommunications(response.data);
                setTotalItems(response.total ?? response.data.length);
            } else {
                console.error('Errore: risposta non valida o vuota:', response);
                setCommunications([]);
                setTotalItems(0);
            }
        } catch (error) {
            console.error('Errore nel recupero delle comunicazioni:', error);
            setCommunications([]);
        } finally {
            setIsLoading(false);
        }
    }, [userId, isDirector, pageSize, setCommunications]);

    useEffect(() => {
        fetchCurrentPage(page);
    }, [fetchCurrentPage, page, location.pathname, isAuthenticated]);

    useEffect(() => {
        const fetchCommunicationViews = async () => {
            try {
                const normalizedCommunications = Array.isArray(communications) ? communications : (communications?.data && Array.isArray(communications.data) ? communications.data : []);

                if (normalizedCommunications.length === 0) {
                    console.warn('Nessuna comunicazione disponibile per recuperare le visualizzazioni.');
                    return;
                }

                const communicationIds = normalizedCommunications.map((communication) => communication.id);
                const views = await getCommunicationViews(communicationIds);
                setCommunicationViews(views);

                if (!Array.isArray(views)) {
                    console.error('La risposta dell\'API non è un array valido:', views);
                    setCommunicationViews({});
                    return;
                }

                const transformedViews = views.reduce((acc, item) => {
                    acc[item.communicationId] = item.views;
                    return acc;
                }, {});

                setCommunicationViews(transformedViews);

            } catch (error) {
                console.error('Errore nel recupero delle visualizzazioni delle comunicazioni:', error);
            }
        };

        if (Array.isArray(communications) && communications.length > 0) {
            fetchCommunicationViews();
        }
    }, [communications]);

    const handleEditCommunication = (id) => {
        const communication = communications.find((c) => c.id === id);
        if (communication) setEditedCommunication({ ...communication });
    };

    const handleCancelEdit = async () => {
        setEditedCommunication(null);
        try {
            await fetchCurrentPage(page);
        } catch (error) {
            console.error('Errore durante l\'aggiornamento delle comunicazioni:', error);
        }
    };

    return (
        <Card elevation={2} padding={24} background="tint1">
            <Heading size={700} marginBottom={16}>
                Comunicazioni
            </Heading>
                    {isAdding && isDirector ? (
                <>
                    <AddCommunicationForm
                        newCommunication={newCommunication}
                        setNewCommunication={setNewCommunication}
                        setIsAdding={setIsAdding}
                        setCommunications={setCommunications}
                        userId={userId}
                                fetchCommunications={fetchCurrentPage}
                                collaborators={collaborators}
                                onCommunicationAdded={() => {
                                    fetchCurrentPage();
                                }}
                    />
                    <Button appearance="primary" marginTop={majorScale(2)} onClick={() => setIsAdding(false)}>
                        Torna indietro <CircleArrowLeftIcon marginLeft={8} />
                    </Button>
                </>
            ) : editedCommunication && isDirector ? (
                <EditCommunicationForm
                    editedCommunication={editedCommunication}
                    setEditedCommunication={setEditedCommunication}
                    handleCancelEdit={handleCancelEdit}
                    fetchCommunications={fetchCurrentPage}
                    userId={userId}
                    setCommunications={setCommunications}
                />
            ) : (
                <>
                    {isLoading ? (
                        <>
                            <Heading size={400} color="muted">
                                Caricamento comunicazioni in corso...
                            </Heading>
                        </>
                    ) : Array.isArray(communications) && communications.length > 0 ? (
                        <>
                            <CommunicationList
                                communications={communications}
                                communicationViews={Object.values(communicationViews).flat()}
                                handleSelectCommunication={isDirector ? handleEditCommunication : undefined}
                                setCommunications={setCommunications}
                                userId={userId}
                                userRole={isOperator ? 'operatore' : isDirector ? 'direttore' : 'educatore'}
                            />
                            <Pane display="flex" justifyContent="center" marginTop={16}>
                                <Pagination
                                    totalPages={Math.max(1, Math.ceil(totalItems / pageSize))}
                                    page={page}
                                    onPageChange={(p) => setPage(p)}
                                    onNextPage={() => setPage((prev) => Math.min(Math.max(1, Math.ceil(totalItems / pageSize)), prev + 1))}
                                    onPreviousPage={() => setPage((prev) => Math.max(1, prev - 1))}
                                />
                            </Pane>
                        </>
                    ) : (
                        <>
                            <Heading size={400} color="muted">
                                Nessuna comunicazione pubblicata.
                            </Heading>
                        </>
                    )}
                    {isDirector && (
                        <Button
                            appearance="primary"
                            intent="success"
                            marginTop={16}
                            onClick={() => setIsAdding(true)}
                        >
                            Aggiungi nuova comunicazione <PlusIcon marginLeft={8} />
                        </Button>
                    )}
                </>
            )}
        </Card>
    );
};

export default Communications;